import React, { useState, useMemo, useContext, useEffect, useCallback, useRef } from "react";
import { Footer, Navbar } from "../components";
import { useSelector, useDispatch } from "react-redux";
import { setCart } from "../redux/action";
import { Link, useNavigate } from "react-router-dom";
import api from "../utils/base_url";
import { clearCart } from "../redux/reducer/handleCart";
import { AuthContext } from "../context/AuthContext";
import { PaymentContext } from "../context/PaymentContext";
import { useCartPricing } from "../hooks/useCartPricing";
import toast from "react-hot-toast";
import "../styles/index.css";
import "../styles/Checkout.css";

// ── Field OUTSIDE Checkout so it is never recreated on re-render ──
const Field = ({ fieldRef, col = "col-sm-6", label, required, children }) => (
  <div ref={fieldRef} className={col}>
    <label className={`form-label${required ? " required" : ""}`}>{label}</label>
    {children}
  </div>
);

const Checkout = () => {
  const cartItems = useSelector((state) => state.handleCart);
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fieldRefs = useRef({});

  // Same endpoint + same hook as Cart.js, called against the same Redux
  // cart items — guarantees the subtotal shown here is identical to the
  // one the customer already saw on the cart page.
  const { pricingMap, subtotal, loading: pricingLoading, error: pricingError } = useCartPricing(cartItems);

  const { setPendingOrderId } = useContext(PaymentContext);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [loadingPincode, setLoadingPincode] = useState(false);
  const [courierReady, setCourierReady] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "", last_name: "", contact_number: "", secondary_number: "",
    billing_door: "", billing_street: "", billing_area: "", billing_landmark: "",
    billing_city: "", billing_state: "", billing_pincode: "",
    shipping_door: "", shipping_street: "", shipping_area: "", shipping_landmark: "",
    shipping_city: "", shipping_state: "", shipping_pincode: "",
    sameAsBilling: false, paymentMethod: "online",
    billing_manual_entry: false, shipping_manual_entry: false,
  });

  const [errors, setErrors] = useState({});
  const [shipping, setShipping] = useState(0);

  // ── Razorpay script ──
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  const fetchCourierCharge = useCallback(async (state) => {
    if (!state) return;
    try {
      setLoadingShipping(true);
      const res = await api.post("courier/calculate/", {
        items: cartItems.map((item) => ({ product: item.id, quantity: item.qty })),
        state,
      });
      setShipping(Number(res.data.courier_charge));
      setCourierReady(true);
    } catch {
      setShipping(0);
      setCourierReady(false);
    } finally {
      setLoadingShipping(false);
    }
  }, [cartItems]);

  const fetchAddressFromPincode = useCallback(async (pincode, type) => {
    if (!/^\d{6}$/.test(pincode)) return;
    try {
      setLoadingPincode(true);
      const response = await fetch(`https://pincode.scaleupstores.com/api/v1/pincode/lookup/${pincode}/`, {
        method: "GET",
        headers: { "Content-Type": "application/json", "X-API-KEY": "my_secure_api_key_2026" },
      });
      const data = await response.json();
      if (data.status) {
        setFormData((prev) => ({
          ...prev,
          [`${type}_city`]: data.district || "",
          [`${type}_state`]: data.state || "",
          [`${type}_manual_entry`]: false,
        }));
        setErrors((prev) => ({ ...prev, [`${type}_city`]: "", [`${type}_state`]: "", [`${type}_pincode`]: "" }));
        if (data.state) { setCourierReady(false); setShipping(0); await fetchCourierCharge(data.state); }
        return;
      }
      toast.error("Pincode not found. Please enter manually.");
      setCourierReady(false); setShipping(0);
      setFormData((prev) => ({ ...prev, [`${type}_city`]: "", [`${type}_state`]: "", [`${type}_manual_entry`]: true }));
    } catch {
      toast.error("Pincode service unavailable. Please enter manually.");
      setCourierReady(false); setShipping(0);
      setFormData((prev) => ({ ...prev, [`${type}_city`]: "", [`${type}_state`]: "", [`${type}_manual_entry`]: true }));
    } finally {
      setLoadingPincode(false);
    }
  }, [fetchCourierCharge]);

  const parseAddress = (address = "") => {
    const parts = address.split(",").map((p) => p.trim());
    let city = "", state = "", pincode = "";
    parts.forEach((p) => {
      const match = p.match(/(.+)\s*-\s*(\d{6})/);
      if (match) { state = match[1].trim(); pincode = match[2]; }
    });
    const pinIndex = parts.findIndex((p) => p.includes(pincode));
    if (pinIndex > 0) city = parts[pinIndex - 1];
    return {
      door: parts[0] || "", street: parts[1] || "", area: parts[2] || "",
      landmark: parts.find((p) => p.toLowerCase().startsWith("landmark"))?.replace("Landmark:", "").trim() || "",
      city, state, pincode,
    };
  };

  const validateCheckout = useCallback(async () => {
    try {
      const res = await api.post("checkout-validate/", { user_id: user.id });
      if (!res.data.status) {
        const qtyMap = {};
        res.data.valid_items.forEach((i) => (qtyMap[i.id] = i.qty));
        const newCart = cartItems.map((item) => qtyMap[item.id] !== undefined ? { ...item, qty: qtyMap[item.id] } : null).filter(Boolean);
        dispatch(setCart(newCart));
        toast.error("Cart updated due to stock/reservation changes");
        navigate("/cart");
      }
    } catch { toast.error("Unable to validate checkout"); navigate("/cart"); }
  }, [cartItems, dispatch, navigate, user.id]);

  useEffect(() => { validateCheckout(); }, [validateCheckout]);

  useEffect(() => {
    if (formData.sameAsBilling && formData.billing_state) {
      setCourierReady(false); setShipping(0); fetchCourierCharge(formData.billing_state);
    }
  }, [formData.sameAsBilling, formData.billing_state, fetchCourierCharge]);

  useEffect(() => {
    if (!user?.customer_details?.id) return;
    (async () => {
      try {
        const res = await api.get(`/customerslist/${user.customer_details.id}/`);
        const data = res.data.data;
        if (!data.address) return;
        const parsed = parseAddress(data.address);
        setFormData((prev) => ({
          ...prev,
          first_name: data.full_name?.split(" ")[0] || "",
          last_name: data.full_name?.split(" ").slice(1).join(" ") || "",
          contact_number: data.contact_number || "",
          secondary_number: data.secondary_number || "",
          billing_door: parsed.door, billing_street: parsed.street, billing_area: parsed.area,
          billing_landmark: parsed.landmark, billing_city: parsed.city,
          billing_state: parsed.state, billing_pincode: parsed.pincode,
          shipping_door: parsed.door, shipping_street: parsed.street, shipping_area: parsed.area,
          shipping_landmark: parsed.landmark, shipping_city: parsed.city,
          shipping_state: parsed.state, shipping_pincode: parsed.pincode,
        }));
        if (/^\d{6}$/.test(parsed.pincode)) { setCourierReady(false); setShipping(0); fetchCourierCharge(parsed.state); }
      } catch (err) { console.error("Failed to load saved address", err); }
    })();
  }, [user, fetchCourierCharge]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if ((name === "contact_number" || name === "secondary_number") && value !== "" && !/^\d+$/.test(value)) return;
    if (name === "sameAsBilling") {
      setFormData((prev) => ({
        ...prev, sameAsBilling: checked,
        shipping_door: checked ? prev.billing_door : "",
        shipping_street: checked ? prev.billing_street : "",
        shipping_area: checked ? prev.billing_area : "",
        shipping_landmark: checked ? prev.billing_landmark : "",
        shipping_city: checked ? prev.billing_city : "",
        shipping_state: checked ? prev.billing_state : "",
        shipping_pincode: checked ? prev.billing_pincode : "",
        shipping_manual_entry: checked ? prev.billing_manual_entry : false,
      }));
    } else {
      setFormData((prev) => {
        const updated = { ...prev, [name]: value };
        if (name === "billing_state" && prev.billing_manual_entry && value.trim().length > 2) {
          setCourierReady(false); setShipping(0); fetchCourierCharge(value.trim());
        }
        if (name === "shipping_state" && prev.shipping_manual_entry && value.trim().length > 2) {
          setCourierReady(false); setShipping(0); fetchCourierCharge(value.trim());
        }
        return updated;
      });
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // subtotal now comes from useCartPricing (server-computed wholesale
  // pricing) instead of a local price * qty reduction, so it matches Cart.
  const { shippingCost, totalAmount, totalItems } = useMemo(() => {
    const shippingCost = Number(shipping) || 0;
    const totalAmount = courierReady ? Math.round(subtotal + shippingCost) : null;
    const totalItems = cartItems.reduce((acc, item) => acc + item.qty, 0);
    return { shippingCost, totalAmount, totalItems };
  }, [cartItems, shipping, courierReady, subtotal]);

  const validateForm = () => {
    const errors = {};
    if (!formData.first_name.trim()) errors.first_name = "First name is required";
    else if (!/^[A-Za-z ]{2,30}$/.test(formData.first_name)) errors.first_name = "Enter a valid first name";
    if (formData.last_name && !/^[A-Za-z ]{1,30}$/.test(formData.last_name)) errors.last_name = "Enter a valid last name";
    if (!/^[6-9]\d{9}$/.test(formData.contact_number)) errors.contact_number = "Enter valid 10-digit mobile number";
    if (formData.secondary_number && !/^[6-9]\d{9}$/.test(formData.secondary_number)) errors.secondary_number = "Enter valid secondary number";
    if (!formData.billing_door.trim()) errors.billing_door = "House / Door No is required";
    if (!formData.billing_street.trim() || formData.billing_street.length < 3) errors.billing_street = "Street must be at least 3 characters";
    if (!formData.billing_area.trim() || formData.billing_area.length < 3) errors.billing_area = "Area must be at least 3 characters";
    if (formData.billing_landmark && formData.billing_landmark.length < 3) errors.billing_landmark = "Landmark must be at least 3 characters";
    if (!/^\d{6}$/.test(formData.billing_pincode)) errors.billing_pincode = "Enter valid 6-digit pincode";
    if (!formData.billing_city?.trim()) errors.billing_city = formData.billing_manual_entry ? "City is required" : "City not detected from pincode";
    if (!formData.billing_state?.trim()) errors.billing_state = formData.billing_manual_entry ? "State is required" : "State not detected from pincode";
    if (!formData.sameAsBilling) {
      if (!formData.shipping_door.trim()) errors.shipping_door = "House / Door No is required";
      if (!formData.shipping_street.trim() || formData.shipping_street.length < 3) errors.shipping_street = "Street must be at least 3 characters";
      if (!formData.shipping_area.trim() || formData.shipping_area.length < 3) errors.shipping_area = "Area must be at least 3 characters";
      if (formData.shipping_landmark && formData.shipping_landmark.length < 3) errors.shipping_landmark = "Landmark must be at least 3 characters";
      if (!/^\d{6}$/.test(formData.shipping_pincode)) errors.shipping_pincode = "Enter valid 6-digit pincode";
      if (!formData.shipping_city?.trim()) errors.shipping_city = formData.shipping_manual_entry ? "City is required" : "City not detected from pincode";
      if (!formData.shipping_state?.trim()) errors.shipping_state = formData.shipping_manual_entry ? "State is required" : "State not detected from pincode";
    }
    return errors;
  };

  const billingFull = [
    formData.billing_door, formData.billing_street, formData.billing_area,
    formData.billing_landmark && `Landmark: ${formData.billing_landmark}`,
    `${formData.billing_city}, ${formData.billing_state} - ${formData.billing_pincode}`, "India",
  ].filter(Boolean).join(", ");

  const shippingFull = formData.sameAsBilling ? billingFull : [
    formData.shipping_door, formData.shipping_street, formData.shipping_area,
    formData.shipping_landmark && `Landmark: ${formData.shipping_landmark}`,
    `${formData.shipping_city}, ${formData.shipping_state} - ${formData.shipping_pincode}`, "India",
  ].filter(Boolean).join(", ");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      const field = fieldRefs.current[firstErrorKey];
      if (field && !field.disabled) { setTimeout(() => { field.scrollIntoView({ block: "center" }); field.focus(); }, 0); }
      toast.error("Please fix the highlighted fields");
      return;
    }
    if (!cartItems.length) { toast.error("Cart is empty!"); return; }
    const finalShippingState = formData.shipping_state || formData.billing_state;
    if (!finalShippingState) { toast.error("Please enter valid pincode to detect state"); return; }
    const itemsPayload = cartItems.map((item) => ({ product: item.id, quantity: item.qty }));
    const orderPayload = {
      customer: user.customer_details.id,
      first_name: formData.first_name, last_name: formData.last_name,
      contact_number: formData.contact_number, secondary_number: formData.secondary_number,
      billing_address: billingFull, shipping_address: shippingFull,
      shipping_state: finalShippingState, payment_method: formData.paymentMethod, items: itemsPayload,
    };
    // NOTE: orderPayload deliberately carries no client-computed prices.
    // orders/reserve/ and orders/create-cod/ independently recompute the
    // wholesale discount server-side — that stays the actual source of
    // truth for what gets charged, regardless of what pricingMap showed.
    if (formData.paymentMethod === "cod") {
      try {
        const res = await api.post("orders/create-cod/", { order_data: orderPayload });
        toast.success("Order placed successfully!");
        dispatch(clearCart());
        navigate(`/order-confirmation/${res.data.order.id}`);
      } catch (err) { toast.error(err.response?.data?.message || "Something went wrong!"); }
      return;
    }
    try {
      const reserveRes = await api.post("orders/reserve/", { items: itemsPayload, payment_method: "online", order_data: orderPayload });
      if (!reserveRes.data.status) { toast.error(reserveRes.data.message); return; }
      const razorpayOrder = reserveRes.data.razorpay_order;
      localStorage.setItem("pendingOrder", razorpayOrder.id);
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: razorpayOrder.amount, currency: razorpayOrder.currency, order_id: razorpayOrder.id,
        name: "Stencils World54", description: "Order Payment",
        handler: function (response) { setProcessingPayment(false); toast.success("Payment successful! Confirming your order…"); setPendingOrderId(razorpayOrder.id); },
        modal: {
          ondismiss: function (reason) {
            if (reason === undefined) {
              api.post("payments/log-frontend-event/", { razorpay_order_id: razorpayOrder.id, event_type: "USER_CANCELLED", error_details: { timestamp: new Date().toISOString() } }).catch(() => { });
              localStorage.removeItem("pendingOrder"); toast.error("Payment cancelled.");
            } else if (reason === "timeout") {
              api.post("payments/log-frontend-event/", { razorpay_order_id: razorpayOrder.id, event_type: "PAYMENT_TIMEOUT", error_details: { timestamp: new Date().toISOString() } }).catch(() => { });
              localStorage.removeItem("pendingOrder"); toast.error("Payment timed out. Please try again.");
            } else { toast.error("Payment failed. Please try again."); }
            setProcessingPayment(false);
          },
        },
        prefill: { name: `${formData.first_name} ${formData.last_name}`, contact: formData.contact_number },
        theme: { color: "#D4AA3A" },
      };
      setProcessingPayment(true);
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        try { rzp.close(); } catch { }
        api.post("payments/log-frontend-event/", {
          razorpay_order_id: response.error.metadata.order_id, event_type: "PAYMENT_FAILED_FRONTEND",
          error_details: { payment_id: response.error.metadata.payment_id, error_code: response.error.code, error_description: response.error.description, error_reason: response.error.reason, timestamp: new Date().toISOString() },
        }).catch(() => { });
        toast.error(response.error.description || "Payment failed"); setProcessingPayment(false);
      });
      rzp.open();
    } catch (err) { toast.error(err.response?.data?.message || "Unable to initiate payment"); }
  };

  const shippingDisplay = () => {
    if (loadingPincode || loadingShipping)
      return <><span className="spinner-border spinner-border-sm me-1" role="status" /> Calculating…</>;
    if (!courierReady) return <span className="shipping-pending">Enter pincode to calculate</span>;
    return `₹${shippingCost}`;
  };

  const submitLabel = () => {
    if (processingPayment) return <><span className="spinner-border spinner-border-sm me-2" />Processing Payment…</>;
    if (pricingLoading) return <><span className="spinner-border spinner-border-sm me-2" />Calculating price…</>;
    if (loadingPincode || loadingShipping) return <><span className="spinner-border spinner-border-sm me-2" />Calculating courier…</>;
    if (!courierReady) return "Enter pincode to place order";
    return `Place Order — ₹${totalAmount}`;
  };

  // helper to pass ref into Field
  const ref = (key) => (el) => { fieldRefs.current[key] = el; };

  if (!cartItems.length) {
    return (
      <>
        <Navbar />
        <div className="checkout-empty">
          <span className="checkout-empty-icon">🛒</span>
          <h4 className="text-theme-dark">Your cart is empty</h4>
          <Link to="/" className="btn-outline-themed">Continue Shopping</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="checkout-page">
        <div className="container">

          <div className="checkout-steps">
            <div className="step-item done">
              <div className="step-dot">✓</div>
              <span>Cart</span>
            </div>
            <div className="step-connector" />
            <div className="step-item active">
              <div className="step-dot">2</div>
              <span>Checkout</span>
            </div>
            <div className="step-connector" />
            <div className="step-item">
              <div className="step-dot">3</div>
              <span>Confirmation</span>
            </div>
          </div>

          <h1 className="checkout-page-title">Checkout</h1>

          <div className="row g-4">

            {/* ── Order Summary ── */}
            <div className="col-lg-4 col-md-5 order-md-last">
              <div className="order-summary-sticky">
                <div className="order-summary-card">
                  <div className="order-summary-header">
                    <span style={{ fontSize: "18px" }}>🧾</span>
                    <h2 className="order-summary-title">Order Summary</h2>
                  </div>
                  <div className="summary-items">
                    {cartItems.map((item) => {
                      const p = pricingMap[item.id];
                      const fallbackPrice =
                        item.offer_price && Number(item.offer_price) > 0
                          ? Number(item.offer_price)
                          : Number(item.price || 0);
                      const unitPrice = p ? p.wholesale_price : fallbackPrice;
                      const hasDiscount = p && p.discount_percent > 0;
                      return (
                        <div key={item.id} className="summary-item">
                          {item.product_image && (
                            <img src={item.product_image} alt={item.product_name} className="summary-item-img" />
                          )}
                          <span className="summary-item-name">{item.product_name}</span>
                          <span className="summary-item-qty">×{item.qty}</span>
                          <span className="summary-item-price">
                            ₹{(unitPrice * item.qty).toLocaleString()}
                            {hasDiscount && (
                              <span className="wholesale-tag"> ({p.discount_percent}% off)</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="summary-line">
                    <span className="summary-line-label">Subtotal ({totalItems} items)</span>
                    <span className="summary-line-value">
                      {pricingLoading ? "Calculating…" : `₹${subtotal.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="summary-line">
                    <span className="summary-line-label">Courier charge</span>
                    <span className="summary-line-value">{shippingDisplay()}</span>
                  </div>
                  <div className="summary-total">
                    <span className="summary-total-label">Total</span>
                    <span className="summary-total-value">
                      {courierReady ? `₹${totalAmount}` : <span className="shipping-pending">after pincode</span>}
                    </span>
                  </div>
                  {!courierReady && !loadingShipping && !loadingPincode && (
                    <div className="courier-hint">
                      <span className="courier-hint-icon">ⓘ</span>
                      <span>Enter your pincode in the form to see the delivery charge and final total.</span>
                    </div>
                  )}
                  {pricingError && (
                    <p className="pricing-error-note">
                      Couldn't refresh live pricing — showing last known prices.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* ── Form ── */}
            <div className="col-lg-8 col-md-7">
              <form onSubmit={handleSubmit} className="checkout-form">

                {/* Contact */}
                <div className="checkout-card mb-4">
                  <div className="checkout-card-header">
                    <div className="checkout-card-header-icon">👤</div>
                    <h2 className="checkout-card-title">Contact Information</h2>
                  </div>
                  <div className="checkout-card-body">
                    <div className="row g-3">
                      <Field fieldRef={ref("first_name")} label="First Name" required>
                        <input type="text" name="first_name" maxLength="15"
                          className={`form-control themed-input${errors.first_name ? " is-invalid" : ""}`}
                          value={formData.first_name} onChange={handleChange} required />
                        {errors.first_name && <div className="error-text">{errors.first_name}</div>}
                      </Field>
                      <Field label="Last Name">
                        <input type="text" name="last_name" maxLength="15"
                          className={`form-control themed-input${errors.last_name ? " is-invalid" : ""}`}
                          value={formData.last_name} onChange={handleChange} />
                        {errors.last_name && <div className="error-text">{errors.last_name}</div>}
                      </Field>
                      <Field fieldRef={ref("contact_number")} label="Contact Number" required>
                        <input type="tel" name="contact_number" maxLength="10"
                          className={`form-control themed-input${errors.contact_number ? " is-invalid" : ""}`}
                          value={formData.contact_number} onChange={handleChange} required />
                        {errors.contact_number && <div className="error-text">{errors.contact_number}</div>}
                      </Field>
                      <Field label="Secondary Contact">
                        <input type="tel" name="secondary_number" maxLength="10"
                          className={`form-control themed-input${errors.secondary_number ? " is-invalid" : ""}`}
                          value={formData.secondary_number} onChange={handleChange} />
                        {errors.secondary_number && <div className="error-text">{errors.secondary_number}</div>}
                      </Field>
                    </div>
                  </div>
                </div>

                {/* Billing Address */}
                <div className="checkout-card mb-4">
                  <div className="checkout-card-header">
                    <div className="checkout-card-header-icon">📍</div>
                    <h2 className="checkout-card-title">Billing Address</h2>
                  </div>
                  <div className="checkout-card-body">
                    <div className="row g-3">
                      <Field fieldRef={ref("billing_door")} label="House / Flat / Door No" required>
                        <input type="text" name="billing_door" maxLength="30"
                          className={`form-control themed-input${errors.billing_door ? " is-invalid" : ""}`}
                          value={formData.billing_door} onChange={handleChange} required />
                        {errors.billing_door && <div className="error-text">{errors.billing_door}</div>}
                      </Field>
                      <Field fieldRef={ref("billing_street")} label="Street / Road Name" required>
                        <input type="text" name="billing_street" maxLength="50"
                          className={`form-control themed-input${errors.billing_street ? " is-invalid" : ""}`}
                          value={formData.billing_street} onChange={handleChange} required />
                        {errors.billing_street && <div className="error-text">{errors.billing_street}</div>}
                      </Field>
                      <Field fieldRef={ref("billing_area")} label="Area / Locality" required>
                        <input type="text" name="billing_area" maxLength="50"
                          className={`form-control themed-input${errors.billing_area ? " is-invalid" : ""}`}
                          value={formData.billing_area} onChange={handleChange} required />
                        {errors.billing_area && <div className="error-text">{errors.billing_area}</div>}
                      </Field>
                      <Field label="Landmark">
                        <input type="text" name="billing_landmark" maxLength="50"
                          className={`form-control themed-input${errors.billing_landmark ? " is-invalid" : ""}`}
                          value={formData.billing_landmark} onChange={handleChange} />
                        {errors.billing_landmark && <div className="error-text">{errors.billing_landmark}</div>}
                      </Field>
                      <Field fieldRef={ref("billing_pincode")} label="Pincode" required>
                        <input type="text" name="billing_pincode" maxLength="6"
                          className={`form-control themed-input${errors.billing_pincode ? " is-invalid" : ""}`}
                          value={formData.billing_pincode}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!/^\d*$/.test(value)) return;
                            if (value.length < 6) { setCourierReady(false); setShipping(0); }
                            setFormData((prev) => ({
                              ...prev, billing_pincode: value,
                              billing_city: "", billing_state: "", billing_manual_entry: false,
                            }));
                            setErrors((prev) => ({ ...prev, billing_pincode: "", billing_city: "", billing_state: "" }));
                            if (value.length === 6) fetchAddressFromPincode(value, "billing");
                          }}
                          required />
                        {errors.billing_pincode && <div className="error-text">{errors.billing_pincode}</div>}
                      </Field>
                      <Field fieldRef={ref("billing_city")} label="City" required>
                        <input type="text" name="billing_city" maxLength="25"
                          className={`form-control themed-input${errors.billing_city ? " is-invalid" : ""}`}
                          value={formData.billing_city} onChange={handleChange}
                          disabled={!formData.billing_manual_entry} />
                        {errors.billing_city && <div className="error-text">{errors.billing_city}</div>}
                      </Field>
                      <Field fieldRef={ref("billing_state")} label="State" required>
                        <input type="text" name="billing_state" maxLength="25"
                          className={`form-control themed-input${errors.billing_state ? " is-invalid" : ""}`}
                          value={formData.billing_state} onChange={handleChange}
                          disabled={!formData.billing_manual_entry} />
                        {errors.billing_state && <div className="error-text">{errors.billing_state}</div>}
                      </Field>
                    </div>
                  </div>
                </div>

                {/* Same-as-billing */}
                <div className="checkout-checkbox-row mb-4">
                  <input type="checkbox" className="form-check-input thick-checkbox"
                    name="sameAsBilling" checked={formData.sameAsBilling}
                    onChange={handleChange} id="sameAsBilling" />
                  <label htmlFor="sameAsBilling" className="form-check-label">
                    Shipping address is the same as billing address
                  </label>
                </div>

                {/* Shipping Address */}
                {!formData.sameAsBilling && (
                  <div className="checkout-card mb-4">
                    <div className="checkout-card-header">
                      <div className="checkout-card-header-icon">🚚</div>
                      <h2 className="checkout-card-title">Shipping Address</h2>
                    </div>
                    <div className="checkout-card-body">
                      <div className="row g-3">
                        <Field fieldRef={ref("shipping_door")} label="House / Flat / Door No" required>
                          <input type="text" name="shipping_door" maxLength="30"
                            className={`form-control themed-input${errors.shipping_door ? " is-invalid" : ""}`}
                            value={formData.shipping_door} onChange={handleChange} required />
                          {errors.shipping_door && <div className="error-text">{errors.shipping_door}</div>}
                        </Field>
                        <Field fieldRef={ref("shipping_street")} label="Street / Road Name" required>
                          <input type="text" name="shipping_street" maxLength="50"
                            className={`form-control themed-input${errors.shipping_street ? " is-invalid" : ""}`}
                            value={formData.shipping_street} onChange={handleChange} required />
                          {errors.shipping_street && <div className="error-text">{errors.shipping_street}</div>}
                        </Field>
                        <Field fieldRef={ref("shipping_area")} label="Area / Locality" required>
                          <input type="text" name="shipping_area" maxLength="50"
                            className={`form-control themed-input${errors.shipping_area ? " is-invalid" : ""}`}
                            value={formData.shipping_area} onChange={handleChange} required />
                          {errors.shipping_area && <div className="error-text">{errors.shipping_area}</div>}
                        </Field>
                        <Field label="Landmark">
                          <input type="text" name="shipping_landmark" maxLength="50"
                            className={`form-control themed-input${errors.shipping_landmark ? " is-invalid" : ""}`}
                            value={formData.shipping_landmark} onChange={handleChange} />
                          {errors.shipping_landmark && <div className="error-text">{errors.shipping_landmark}</div>}
                        </Field>
                        <Field fieldRef={ref("shipping_pincode")} label="Pincode" required>
                          <input type="text" name="shipping_pincode" maxLength="6"
                            className={`form-control themed-input${errors.shipping_pincode ? " is-invalid" : ""}`}
                            value={formData.shipping_pincode}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (!/^\d*$/.test(value)) return;
                              if (value.length < 6) { setCourierReady(false); setShipping(0); }
                              setFormData((prev) => ({
                                ...prev, shipping_pincode: value,
                                shipping_city: "", shipping_state: "", shipping_manual_entry: false,
                              }));
                              setErrors((prev) => ({ ...prev, shipping_pincode: "", shipping_city: "", shipping_state: "" }));
                              if (value.length === 6) fetchAddressFromPincode(value, "shipping");
                            }}
                            required />
                          {errors.shipping_pincode && <div className="error-text">{errors.shipping_pincode}</div>}
                        </Field>
                        <Field fieldRef={ref("shipping_city")} label="City" required>
                          <input type="text" name="shipping_city" maxLength="25"
                            className={`form-control themed-input${errors.shipping_city ? " is-invalid" : ""}`}
                            value={formData.shipping_city} onChange={handleChange}
                            disabled={!formData.shipping_manual_entry} />
                          {errors.shipping_city && <div className="error-text">{errors.shipping_city}</div>}
                        </Field>
                        <Field fieldRef={ref("shipping_state")} label="State" required>
                          <input type="text" name="shipping_state" maxLength="25"
                            className={`form-control themed-input${errors.shipping_state ? " is-invalid" : ""}`}
                            value={formData.shipping_state} onChange={handleChange}
                            disabled={!formData.shipping_manual_entry} />
                          {errors.shipping_state && <div className="error-text">{errors.shipping_state}</div>}
                        </Field>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="btn-themed"
                  disabled={
                    processingPayment ||
                    pricingLoading ||
                    !courierReady ||
                    loadingShipping ||
                    loadingPincode ||
                    !formData.billing_state ||
                    (!formData.sameAsBilling && !formData.shipping_state)
                  }
                >
                  {submitLabel()}
                </button>

              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Checkout;